import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  GridLegacy as Grid,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import DateTimeWidget from '~/components/Common/Elements/DateTimeWidget';
import Spinner from '~/components/Common/Spinner';
import InputCruiseLineSelect from '~/components/Cruises/components/Selects/InputCruiseLineSelect';
import { validateBundleCabinMarkupRows } from '~/components/Cruises/pages/Bundles/bundle-cabin-markup-form-utils';
import {
  BUNDLE_ELIGIBILITY_TYPES,
  BUNDLE_TYPES,
  bundleFormToApi,
  changeBundleFormType,
  getBundleFormState,
} from '~/components/Cruises/pages/Bundles/bundle-form-utils';
import BundleCabinTypeMarkupsEditor from '~/components/Cruises/pages/Bundles/components/BundleCabinTypeMarkupsEditor';
import { CruiseBundle } from '~/components/Cruises/pages/Bundles/types';

import { useSaveBundle } from '~/hooks/apis/cruises/useSaveBundle';
import useAppNavigation from '~/hooks/useAppNavigation';

import { CruiseLine } from '~/services/cruises/CruiseLineService';

type Props = {
  bundle?: CruiseBundle;
};

function BundleForm({ bundle }: Props) {
  const [formValues, setFormValues] = useState(() => getBundleFormState(bundle));

  const { enqueueSnackbar } = useSnackbar();
  const { push } = useAppNavigation();
  const isEditing = !!bundle;
  const isPreconfigured = formValues.type === BUNDLE_TYPES.PRECONFIGURED;
  const { cabinMarkupRows } = formValues;

  const saveBundleMutation = useSaveBundle();

  const isSaveEnabled = useMemo(
    () =>
      Boolean(
        formValues.name &&
        formValues.startDate &&
        formValues.endDate &&
        formValues.fkVendorId &&
        (!isPreconfigured || !validateBundleCabinMarkupRows(cabinMarkupRows)) &&
        !saveBundleMutation.isPending,
      ),
    [formValues, cabinMarkupRows, isPreconfigured, saveBundleMutation.isPending],
  );

  const handleChangeField = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name) {
      setFormValues((prev) => ({ ...prev, [name]: value ?? undefined }));
    }
  }, []);

  const handleChangeType = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const type = event.target.value;
      if (!isEditing && (type === BUNDLE_TYPES.IN_FLOW_SELECTION || type === BUNDLE_TYPES.PRECONFIGURED)) {
        setFormValues((prev) => changeBundleFormType(prev, type));
      }
    },
    [isEditing],
  );

  const handleChangeNumberField = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { name, valueAsNumber } = event.target;
    if (name) {
      setFormValues((prev) => ({ ...prev, [name]: isNaN(valueAsNumber) ? '' : valueAsNumber }));
    }
  }, []);

  const handleChangeDateField = useCallback((value: string, name: 'startDate' | 'endDate') => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleCruiseLineField = useCallback((cruiseLine: CruiseLine) => {
    setFormValues((prev) => ({ ...prev, fkVendorId: cruiseLine?.id ?? null }));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (formValues.startDate && formValues.endDate && formValues.endDate <= formValues.startDate) {
      enqueueSnackbar('End date must be after start date', { variant: 'error' });
      return;
    }

    const cabinMarkupError = isPreconfigured ? validateBundleCabinMarkupRows(cabinMarkupRows) : undefined;
    if (cabinMarkupError) {
      enqueueSnackbar(cabinMarkupError, { variant: 'error' });
      return;
    }

    const sharedFields = bundleFormToApi(formValues);

    const apiData = isEditing ? sharedFields : { ...sharedFields, fkVendorId: formValues.fkVendorId || '' };

    saveBundleMutation.mutate(
      { id: bundle?.id, data: apiData },
      {
        onSuccess: (result) => {
          enqueueSnackbar('Bundle saved successfully', { variant: 'success' });
          const bundleId = bundle?.id || result?.id;
          const tab =
            formValues.type === BUNDLE_TYPES.IN_FLOW_SELECTION &&
            formValues.eligibilityType === BUNDLE_ELIGIBILITY_TYPES.EXPLICIT_RATES
              ? 'filters'
              : 'items';
          push(`/cruises/bundles/edit/${bundleId}/${tab}`);
        },
        onError: () => {
          enqueueSnackbar('Error saving bundle', { variant: 'error' });
        },
      },
    );
  }, [bundle?.id, cabinMarkupRows, isPreconfigured, isEditing, enqueueSnackbar, push, formValues, saveBundleMutation]);

  useEffect(() => {
    setFormValues(getBundleFormState(bundle));
  }, [bundle]);

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            required
            fullWidth
            name="name"
            label="Bundle Name"
            onChange={handleChangeField}
            inputProps={{ maxLength: 100 }}
            value={formValues.name}
            helperText="e.g. Sydney Pre & Post Stay Bundle"
          />
        </Grid>
        <Grid item xs={6}>
          <InputCruiseLineSelect
            required
            label="Cruise line"
            value={formValues.fkVendorId}
            onChange={handleCruiseLineField}
            withApprovedOffers
            disabled={isEditing || saveBundleMutation.isPending}
          />
        </Grid>
      </Grid>

      <FormControl component="fieldset" fullWidth margin="normal" disabled={isEditing || saveBundleMutation.isPending}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <FormLabel id="bundle-type-label">Type</FormLabel>
          <Tooltip
            title={
              <Stack spacing={1}>
                <Typography variant="body2">
                  In-flow selection: Customers choose their hotel during booking. Applies to all net rates or selected
                  rates.
                </Typography>
                <Typography variant="body2">
                  Preconfigured: Hotels, eligible rates, cabin markups and flight credits are configured in advance.
                </Typography>
              </Stack>
            }
          >
            <IconButton size="small" aria-label="About bundle types">
              <InfoOutlinedIcon fontSize="small" color="primary" />
            </IconButton>
          </Tooltip>
        </Stack>
        <RadioGroup
          row
          name="type"
          aria-labelledby="bundle-type-label"
          value={formValues.type}
          onChange={handleChangeType}
        >
          <FormControlLabel value={BUNDLE_TYPES.IN_FLOW_SELECTION} control={<Radio />} label="In-flow selection" />
          <FormControlLabel value={BUNDLE_TYPES.PRECONFIGURED} control={<Radio />} label="Preconfigured" />
        </RadioGroup>
      </FormControl>

      {isPreconfigured && (
        <Grid container spacing={2} mt={1}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              name="savingsAmount"
              label="Savings Amount ($)"
              type="number"
              onChange={handleChangeNumberField}
              value={formValues.savingsAmount ?? ''}
              helperText="Amount customers save by booking this bundle (display only)"
              inputProps={{ min: 0, step: 1 }}
            />
          </Grid>
        </Grid>
      )}

      <Stack mt={2} spacing={2} direction="row">
        <DateTimeWidget
          defaultTimezone="Australia/Sydney"
          disablePast
          format="DD/MM/YYYY HH:mm"
          label="Bundle Start Date *"
          value={formValues.startDate}
          onChange={(value) => handleChangeDateField(value, 'startDate')}
        />

        <DateTimeWidget
          defaultTimezone="Australia/Sydney"
          disablePast
          format="DD/MM/YYYY HH:mm"
          label="Bundle End Date *"
          value={formValues.endDate}
          onChange={(value) => handleChangeDateField(value, 'endDate')}
        />
      </Stack>

      <Grid container spacing={2} mt={1}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            name="termsAndConditions"
            label="Terms & Conditions"
            multiline
            minRows={3}
            onChange={handleChangeField}
            value={formValues.termsAndConditions ?? ''}
            helperText="Bundle terms and conditions displayed to the customer (optional)"
          />
        </Grid>
      </Grid>

      {isPreconfigured ? (
        <BundleCabinTypeMarkupsEditor
          rows={cabinMarkupRows}
          onChange={(rows) => setFormValues((prev) => ({ ...prev, cabinMarkupRows: rows }))}
          disabled={saveBundleMutation.isPending}
        />
      ) : (
        <FormControl component="fieldset" fullWidth margin="normal" disabled={saveBundleMutation.isPending}>
          <FormLabel id="bundle-eligibility-label">Eligibility</FormLabel>
          <RadioGroup
            row
            name="eligibilityType"
            aria-labelledby="bundle-eligibility-label"
            value={formValues.eligibilityType}
            onChange={handleChangeField}
          >
            <FormControlLabel
              value={BUNDLE_ELIGIBILITY_TYPES.ALL_NET_RATES}
              control={<Radio />}
              label="All net rates"
            />
            <FormControlLabel
              value={BUNDLE_ELIGIBILITY_TYPES.EXPLICIT_RATES}
              control={<Radio />}
              label="Explicit rates"
            />
          </RadioGroup>
        </FormControl>
      )}

      <Box mt={2} display="flex" justifyContent="flex-end">
        <Button variant="contained" onClick={handleSubmit} disabled={!isSaveEnabled}>
          {saveBundleMutation.isPending && <Spinner size={15} />}
          Save Changes
        </Button>
      </Box>
    </Box>
  );
}

export default BundleForm;
